import * as React from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

/**
 * Banner — a short message about what just happened, or what needs attention,
 * shown in the flow of the page (not floating over it).
 *
 *   info     a plain notice; nothing has gone wrong
 *   success  it worked
 *   warning  needs attention, but nothing is broken
 *   error    something failed
 *
 * Colour is never the only signal: each variant carries its own icon, and the
 * urgent two announce themselves to screen readers (role="alert") while the
 * calm two are polite (role="status").
 */
const bannerVariants = cva(
  "flex w-full items-start gap-3 rounded-card border p-4 text-sm leading-relaxed text-ink",
  {
    variants: {
      variant: {
        info: "border-info bg-info/10",
        success: "border-success bg-success/10",
        warning: "border-warning bg-warning/10",
        error: "border-error bg-error/10",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

type BannerTone = NonNullable<VariantProps<typeof bannerVariants>["variant"]>;

/** The tint the icon wears — full strength, against the 10% wash behind it. */
const toneMark: Record<BannerTone, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
};

/** The default icon per tone, so a banner reads correctly without one passed. */
const toneIcon: Record<BannerTone, React.ReactNode> = {
  info: <Info className="h-5 w-5" />,
  success: <CheckCircle2 className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
  error: <XCircle className="h-5 w-5" />,
};

export interface BannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof bannerVariants> {
  /** Optional bold line above the message. */
  title?: string;
  /** Overrides the tone's own icon. Pass `null` for no icon at all. */
  icon?: React.ReactNode;
  /** When given, a dismiss button appears at the end of the banner. */
  onDismiss?: () => void;
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, variant, title, icon, onDismiss, children, ...props }, ref) => {
    const tone: BannerTone = variant ?? "info";
    const mark = icon === undefined ? toneIcon[tone] : icon;

    return (
      <div
        ref={ref}
        // Urgent tones interrupt; calm ones wait their turn. A caller can still
        // override with its own `role`.
        role={tone === "warning" || tone === "error" ? "alert" : "status"}
        className={cn(bannerVariants({ variant }), className)}
        {...props}
      >
        {mark && (
          <span aria-hidden="true" className={cn("mt-0.5 shrink-0", toneMark[tone])}>
            {mark}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {title && <p className="font-semibold text-ink">{title}</p>}
          {children && <div className={cn(title && "mt-1")}>{children}</div>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="-m-1 shrink-0 rounded p-1 text-muted transition-colors hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);
Banner.displayName = "Banner";

export { bannerVariants };
